import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, Phone, Mail, MessageCircle, Calendar } from 'lucide-react';
import { company } from '../config/company';

const GA_ID = 'G-K656E4E0L8';

const CTA_LABELS = {
  info: {
    pl: { title: 'Poproś o więcej informacji', btn: 'Wyślij zapytanie', placeholderMsg: 'Zadaj tutaj pytanie o tę nieruchomość...' },
  },
  viewing: {
    pl: { title: 'Umów oglądanie', btn: 'Zaplanuj oglądanie', placeholderMsg: 'Preferowany termin i ewentualne uwagi...' },
  },
  callback: {
    pl: { title: 'Oddzwońcie do mnie', btn: 'Poproś o oddzwonienie', placeholderMsg: 'Kiedy najlepiej możemy się z Tobą skontaktować?' },
  },
};

const PropertyLeadForm = ({ property, defaultCta = 'info' }) => {
  const navigate = useNavigate();
  const [cta, setCta] = useState(defaultCta);
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [optin, setOptin] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const labels = CTA_LABELS[cta].pl;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.first_name.trim() || !form.email.trim()) {
      setError('Wprowadź przynajmniej imię i adres e-mail.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          source: 'property_detail',
          cta_type: cta,
          newsletter_optin: optin,
          property_id: property.id,
          property_ref: property.ref || property.id,
          property_title: property.title,
          property_location: property.location,
          property_region: property.region,
          property_price: property.price,
          property_url: typeof window !== 'undefined' ? window.location.href : '',
        }),
      });
      if (!res.ok) throw new Error('submit failed');

      if (typeof window.gtag === 'function') {
        window.gtag('event', 'generate_lead', {
          send_to: GA_ID,
          form_location: 'property_detail',
          cta_type: cta,
          property_id: property.id,
          value: 1,
          currency: 'EUR',
        });
      }

      // Tell the retention popup we converted — it should NOT pester this user
      // when the parent modal closes after submission.
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('casaothers:lead-submitted'));
      }

      setDone(true);
      // Redirect to the central thank-you page so Google Ads can count the
      // conversion against /bedankt regardless of which CTA was used.
      navigate('/bedankt');
    } catch {
      setError('Coś poszło nie tak. Spróbuj ponownie.');
    } finally {
      setSubmitting(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  if (done) {
    return (
      <div style={styles.success} data-testid="property-lead-success">
        <CheckCircle2 size={48} color="#C5A059" />
        <h3 style={styles.successTitle}>
          Dziękujemy za Twoje zapytanie!
        </h3>
        <p style={styles.successText}>
          Skontaktujemy się z Tobą jak najszybciej. Wolisz kontakt bezpośredni?
        </p>
        <a
          href={company.whatsappUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={styles.whatsappBtn}
          data-testid="property-lead-whatsapp"
        >
          <MessageCircle size={18} />
          WhatsApp
        </a>
      </div>
    );
  }

  return (
    <div style={styles.wrapper} data-testid="property-lead-form-wrapper">
      <div style={styles.tabs}>
        {Object.entries(CTA_LABELS).map(([key, val]) => {
          const Icon = key === 'info' ? Mail : key === 'viewing' ? Calendar : Phone;
          return (
            <button
              key={key}
              type="button"
              onClick={() => setCta(key)}
              style={cta === key ? styles.tabActive : styles.tab}
              data-testid={`property-cta-${key}`}
            >
              <Icon size={16} />
              <span>{val.pl.title}</span>
            </button>
          );
        })}
      </div>

      <form style={styles.form} onSubmit={onSubmit} data-testid="property-lead-form">
        <h3 style={styles.title}>{labels.title}</h3>
        <p style={styles.context}>
          O tej nieruchomości: <strong>{property.title}</strong>
          {property.ref && property.ref !== property.id && ` (Ref. ${property.ref})`}
        </p>

        <div style={styles.intlNote} data-testid="lead-form-intl-note">
          <span style={styles.intlTag}>ES</span>
          <span>
            <strong>Nasz międzynarodowy zespół w Hiszpanii skontaktuje się z Tobą po angielsku.</strong><br />
            <span style={styles.intlNoteEn}>Our international team in Spain will contact you in English.</span>
          </span>
        </div>

        <div style={styles.row}>
          <input
            type="text"
            placeholder="Imię *"
            value={form.first_name}
            onChange={set('first_name')}
            style={styles.input}
            required
            autoComplete="given-name"
            data-testid="lead-firstname-input"
          />
          <input
            type="text"
            placeholder="Nazwisko"
            value={form.last_name}
            onChange={set('last_name')}
            style={styles.input}
            autoComplete="family-name"
            data-testid="lead-lastname-input"
          />
        </div>
        <div style={styles.row}>
          <input
            type="email"
            placeholder="Adres e-mail *"
            value={form.email}
            onChange={set('email')}
            style={styles.input}
            required
            autoComplete="email"
            data-testid="lead-email-input"
          />
          <input
            type="tel"
            placeholder="Numer telefonu"
            value={form.phone}
            onChange={set('phone')}
            style={styles.input}
            autoComplete="tel"
            data-testid="lead-phone-input"
          />
        </div>
        <textarea
          placeholder={labels.placeholderMsg}
          value={form.message}
          onChange={set('message')}
          style={{ ...styles.input, minHeight: '110px', resize: 'vertical' }}
          data-testid="lead-message-input"
        />
        {error && <p style={styles.error}>{error}</p>}
        <label style={styles.optinLabel} data-testid="property-optin-wrap">
          <input
            type="checkbox"
            checked={optin}
            onChange={(e) => setOptin(e.target.checked)}
            style={styles.optinCheckbox}
            data-testid="property-optin-checkbox"
          />
          <span>
            Tak, informujcie mnie również przez newsletter CasaOthers (maks. 2× w miesiącu, w każdej chwili można się wypisać).
          </span>
        </label>
        <button
          type="submit"
          disabled={submitting}
          style={{ ...styles.btn, ...(submitting ? { opacity: 0.6, cursor: 'not-allowed' } : {}) }}
          data-testid="lead-submit-button"
        >
          {submitting ? 'Wysyłanie...' : labels.btn}
        </button>
        <p style={styles.privacy}>
          Twoje dane są wykorzystywane wyłącznie do odpowiedzi na Twoje zapytanie. Przeczytaj naszą{' '}
          <a href="/privacy" style={styles.link}>politykę prywatności</a>.
        </p>
      </form>
    </div>
  );
};

const styles = {
  intlNote: {
    display: 'flex', gap: '0.6rem', alignItems: 'flex-start',
    padding: '0.65rem 0.8rem', background: '#F6F1E7', border: '1px solid #E7DCC4',
    marginBottom: '1rem', fontSize: '0.78rem', lineHeight: 1.5, color: '#585755',
  },
  intlTag: {
    flexShrink: 0, fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.05em',
    background: '#1A1918', color: '#C5A059', padding: '0.15rem 0.4rem', marginTop: '0.1rem',
  },
  intlNoteEn: { color: '#8C8B88', fontStyle: 'italic' },

  wrapper: {
    backgroundColor: 'white',
    border: '1px solid #EAE5DD',
    overflow: 'hidden',
  },
  tabs: {
    display: 'flex',
    flexWrap: 'wrap',
    borderBottom: '1px solid #EAE5DD',
  },
  tab: {
    flex: '1 1 0',
    minWidth: '120px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5rem',
    backgroundColor: '#FAF9F6',
    color: '#585755',
    border: 'none',
    padding: '1rem 0.75rem',
    fontSize: '0.8rem',
    cursor: 'pointer',
    fontWeight: 500,
    textAlign: 'center',
  },
  tabActive: {
    flex: '1 1 0',
    minWidth: '120px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5rem',
    backgroundColor: 'white',
    color: '#C5A059',
    border: 'none',
    borderBottomWidth: '2px',
    borderBottomStyle: 'solid',
    borderBottomColor: '#C5A059',
    padding: '1rem 0.75rem',
    fontSize: '0.8rem',
    cursor: 'pointer',
    fontWeight: 600,
    textAlign: 'center',
  },
  form: { padding: '2rem', display: 'flex', flexDirection: 'column', gap: '0.85rem' },
  title: {
    fontFamily: "'Playfair Display', serif",
    fontSize: '1.5rem',
    color: '#1A1918',
    margin: '0',
  },
  context: {
    color: '#585755',
    fontSize: '0.85rem',
    margin: '0 0 0.5rem',
    paddingBottom: '0.85rem',
    borderBottom: '1px solid #EAE5DD',
  },
  row: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0.75rem' },
  input: {
    padding: '0.85rem 1rem',
    border: '1px solid #EAE5DD',
    backgroundColor: '#FAF9F6',
    color: '#1A1918',
    fontSize: '0.95rem',
    fontFamily: "'Manrope', sans-serif",
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box',
  },
  btn: {
    backgroundColor: '#C5A059',
    color: 'white',
    border: 'none',
    padding: '1rem 1.5rem',
    fontSize: '0.875rem',
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    cursor: 'pointer',
    fontWeight: 500,
    marginTop: '0.5rem',
  },
  error: { color: '#9E3E3E', fontSize: '0.875rem', margin: 0 },
  optinLabel: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '0.55rem',
    color: '#585755',
    fontSize: '0.8rem',
    lineHeight: 1.5,
    cursor: 'pointer',
    margin: '0.25rem 0',
  },
  optinCheckbox: {
    marginTop: '0.15rem',
    width: '16px',
    height: '16px',
    accentColor: '#C5A059',
    flexShrink: 0,
    cursor: 'pointer',
  },
  privacy: { color: '#8C8B88', fontSize: '0.75rem', margin: '0.5rem 0 0', lineHeight: 1.5 },
  link: { color: '#C5A059', textDecoration: 'underline' },
  success: {
    backgroundColor: 'white',
    border: '1px solid #EAE5DD',
    padding: '3rem 2rem',
    textAlign: 'center',
  },
  successTitle: {
    fontFamily: "'Playfair Display', serif",
    fontSize: '1.5rem',
    color: '#1A1918',
    margin: '1rem 0 0.75rem',
  },
  successText: { color: '#585755', fontSize: '0.95rem', lineHeight: 1.6, margin: '0 0 1.5rem' },
  whatsappBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.5rem',
    backgroundColor: '#25D366',
    color: 'white',
    padding: '0.85rem 1.5rem',
    textDecoration: 'none',
    fontSize: '0.875rem',
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    fontWeight: 500,
  },
};

export default PropertyLeadForm;
